import { Component, OnInit, OnDestroy, Input, Inject } from '@angular/core';
import { PayCustomerAction, BankNotificationCodeType, PaymentType } from '../../../../../app/shared/enums/smarthub-enums';
import { PayInfo, PayItemInfo } from '../../../../../app/services/pay-info.model';
import { BehaviorSubject, Observable } from 'rxjs';
import { DataSource } from '@angular/cdk/table';
import { TranslateService } from '@ngx-translate/core';
import { BankNotificationCodeService } from '../../../../../app/services/bank-notification-code.service';
import { BankNotificationCode } from '../../../../../app/services/bank-notification-code.model';
import * as _ from 'lodash';
import { PayPortionActionInfo } from '../../../../../app/services/pay-portion-action-info.model';
import { DisplayPayItemInfo } from '../pay-info-table.component';
import { FeatureToggleService } from '../../../../shared/services/feature-toggle/feature-toggle.service';
import { SettingsService } from '../../../../shared/services/settings/settings.service';

export class PayInfoActionConfirmationInfo {
  private _isValid: boolean;
  private _owner: PayInfoActionConfirmationComponent;

  get isStop() {
    return this.action === PayCustomerAction.Stop;
  }

  get isCancelStop() {
    return this.action === PayCustomerAction.CancelStop;
  }

  get isValid() {
    return this._isValid;
  }

  constructor(public action: PayCustomerAction, public pays: DisplayPayItemInfo[]) { }

  setOwner(owner: PayInfoActionConfirmationComponent) {
    this._owner = owner;
  }

  getPortionInfos(): PayPortionActionInfo[] {
    return this._owner.dataSource.source.value.map(p =>
      new PayPortionActionInfo(p.pay.id, p.payItem.paymentType, p.payItem.itemNumber, p.reasonCode, p.comment));
  }

  setValid(value: boolean) {
    this._isValid = value;
  }
}

class TranslatedBankNotificationCode {
  translatedDescription: string;

  constructor(public original: BankNotificationCode) { }
}

class PayItemConfirmationInfo {
  private _reasonCode: string;
  comment: string;

  get reasonCode() {
    return this._reasonCode;
  }
  set reasonCode(value: string) {
    this._reasonCode = value;
    this._validationCallback();
  }

  get isCheck() {
    return this.payItem.paymentType === PaymentType.Check;
  }

  get isDirectDeposit() {
    return this.payItem.paymentType === PaymentType.DirectDeposit;
  }

  constructor(public pay: PayInfo, public payItem: PayItemInfo, public displayName: string, private _validationCallback: () => void) { }
}

class ConfirmationInfoDataSource extends DataSource<PayItemConfirmationInfo> {
  source: BehaviorSubject<PayItemConfirmationInfo[]>;

  constructor() {
    super();
    this.source = new BehaviorSubject<PayItemConfirmationInfo[]>([]);
  }

  connect(): Observable<PayItemConfirmationInfo[]> {
    return this.source;
  }

  disconnect() { }
}

const infoNotificationType = 'info';

@Component({
  selector: 'ssg-pay-info-action-confirmation',
  templateUrl: './pay-info-action-confirmation.component.html',
  styleUrls: ['./pay-info-action-confirmation.component.less']
})
export class PayInfoActionConfirmationComponent implements OnInit, OnDestroy {

  private _confirmationInfo: PayInfoActionConfirmationInfo;
  private _notificationCodes: TranslatedBankNotificationCode[];
  private _checkNotificationCodes: TranslatedBankNotificationCode[] = [];
  private _ddNotificationCodes: TranslatedBankNotificationCode[] = [];
  private _checkNotificationCodeTranslations = {};
  private _ddNotificationCodeTranslations = {};
  private _hasCheckItems: boolean;
  private _hasDDItems: boolean;

  dataSource: ConfirmationInfoDataSource;
  isBulkEditing: boolean;

  allColumns = [
    'removePay',
    'displayName',
    'pay.payUtcDate',
    'payItem.itemNumber',
    'payItem.paymentType',
    'payItem.amount',
    'reasonCode',
    'comment'
  ];

  @Input()
  set confirmationInfo(value: PayInfoActionConfirmationInfo) {
    this._confirmationInfo = value;

    if (!value) {
      return;
    }

    value.setOwner(this);
    this._updateSelectableReasonCodes();
    this.dataSource.source.next(this._processPays(value.pays));
  }
  get confirmationInfo(): PayInfoActionConfirmationInfo {
    return this._confirmationInfo;
  }

  get bulkCheckCode(): string {
    return this._getBulkCode(PaymentType.Check);
  }
  set bulkCheckCode(value: string) {
    this._setBulkCode(PaymentType.Check, this._checkNotificationCodes, value);
  }

  get bulkDDCode(): string {
    return this._getBulkCode(PaymentType.DirectDeposit);
  }
  set bulkDDCode(value: string) {
    this._setBulkCode(PaymentType.DirectDeposit, this._ddNotificationCodes, value);
  }

  get checkNotificationCodes() {
    return this._checkNotificationCodes;
  }

  get ddNotificationCodes() {
    return this._ddNotificationCodes;
  }

  get hasCheckItems() {
    return this._hasCheckItems;
  }

  get hasDDItems() {
    return this._hasDDItems;
  }

  constructor(
    @Inject(TranslateService) private _translateService: TranslateService,
    @Inject(BankNotificationCodeService) private _notificationCodeService: BankNotificationCodeService,
    @Inject(FeatureToggleService) private readonly _productConfigService: FeatureToggleService,
  ) {

    this.dataSource = new ConfirmationInfoDataSource();
    this.isBulkEditing = false;
  }

  ngOnInit() {
    this._notificationCodeService.getCodes().subscribe(codes => {
      this._notificationCodes = this._translateCodes(codes);
      this._updateSelectableReasonCodes();
      this._updatePayReasonCodes(this.dataSource.source.value);
    });
  }

  ngOnDestroy() {
  }

  removePay(pay: PayItemConfirmationInfo) {
    const index = this.dataSource.source.value.indexOf(pay);
    const pays = this.dataSource.source.value;
    this.dataSource.source.value.splice(index, 1);
    this.dataSource.source.next(pays);
    this._updateValidation();

    this._hasCheckItems = pays.some(p => p.isCheck);
    this._hasDDItems = pays.some(p => p.isDirectDeposit);
  }

  getPaymentTypeTextKey(type: PaymentType): string {
    switch (type) {
      case PaymentType.Check:
        return 'EMPLOYEE_PAYS.PAYMENT_TYPE_CHECK';
      case PaymentType.DirectDeposit:
        return 'EMPLOYEE_PAYS.PAYMENT_TYPE_DIRECT_DEPOSIT';
      default:
        return 'EMPLOYEE_PAYS.PAYMENT_TYPE_UNKNOWN';
    }
  }

  translateReasonCode(pay: PayItemConfirmationInfo) {
    let translation: string;
    if (pay.isCheck) {
      translation = this._checkNotificationCodeTranslations[pay.reasonCode];
    } else if (pay.isDirectDeposit) {
      translation = this._ddNotificationCodeTranslations[pay.reasonCode];
    }

    return translation || pay.reasonCode;
  }

  enableStopPaymentInfoBox() {
    return this._productConfigService.isFeatureEnabled('enableStopPaymentInfoBox');
  }

  private _processPays(pays: DisplayPayItemInfo[]): PayItemConfirmationInfo[] {
    const mappedPays = pays.map(p => new PayItemConfirmationInfo(
      p.pay,
      p.payItem,
      this._translateService.instant(
        'EMPLOYEE_PAYS.EMPLOYEE_FULL_NAME', { firstName: p.pay.employee.firstName, lastName: p.pay.employee.lastName }),
      this._updateValidation.bind(this)
    ));

    this._updatePayReasonCodes(mappedPays);

    this._hasCheckItems = mappedPays.some(p => p.isCheck);
    this._hasDDItems = mappedPays.some(p => p.isDirectDeposit);

    return mappedPays;
  }

  private _updateValidation() {
    if (!this._confirmationInfo) {
      return;
    }

    if (!this.dataSource || !this.dataSource.source.value || !this.dataSource.source.value.length) {
      this._confirmationInfo.setValid(false);
    }

    this._confirmationInfo.setValid(this.dataSource.source.value.length
      && this.dataSource.source.value.every(p => p.reasonCode && p.reasonCode.length));
  }

  private _translateCodes(codes: BankNotificationCode[]): TranslatedBankNotificationCode[] {
    return codes.map(c => {
      const translated = new TranslatedBankNotificationCode(c);
      const key = `BANK_NOTIFICATION_CODE.${c.code}`;
      translated.translatedDescription = this._translateService.instant(key);
      if (translated.translatedDescription === `[${key}]`) {
        translated.translatedDescription = c.desc;
      }
      return translated;
    });
  }

  private _updateSelectableReasonCodes() {
    this._checkNotificationCodes = [];
    this._ddNotificationCodes = [];

    if (!this.confirmationInfo || !this._notificationCodes) {
      return;
    }

    let checkType: BankNotificationCodeType; let ddType: BankNotificationCodeType;
    if (this.confirmationInfo.isStop) {
      checkType = BankNotificationCodeType.CheckCancellation;
      ddType = BankNotificationCodeType.DirectDepositCancellation;
    } else if (this.confirmationInfo.isCancelStop) {
      checkType = BankNotificationCodeType.CheckUndoCancellation;
      ddType = BankNotificationCodeType.DirectDepositUndoCancellation;
    } else {
      return;
    }

    this._checkNotificationCodes = _.filter(this._notificationCodes, c => c.original.type === checkType);
    this._ddNotificationCodes = _.filter(this._notificationCodes, c => c.original.type === ddType);

    const createTranslationMap = (codes: TranslatedBankNotificationCode[]) => {
      const result = {};
      for (const code of codes) {
        result[code.original.code] = code.translatedDescription;
      }

      return result;
    };
    this._checkNotificationCodeTranslations = createTranslationMap(this._checkNotificationCodes);
    this._ddNotificationCodeTranslations = createTranslationMap(this._ddNotificationCodes);
  }

  private _updatePayReasonCodes(pays: PayItemConfirmationInfo[]) {
    let checkCode: string; let ddCode: string;
    if (this._checkNotificationCodes.length === 1) {
      checkCode = this._checkNotificationCodes[0].original.code;
    } else {
      checkCode = null;
    }
    if (this._ddNotificationCodes.length === 1) {
      ddCode = this._ddNotificationCodes[0].original.code;
    } else {
      ddCode = null;
    }

    for (const pay of pays) {
      if (pay.isCheck) {
        if (checkCode) {
          pay.reasonCode = checkCode;
        } else if (!this._checkNotificationCodes.find(c => c.original.code === pay.reasonCode)) {
          pay.reasonCode = null;
        }
      } else if (pay.isDirectDeposit) {
        if (ddCode) {
          pay.reasonCode = ddCode;
        } else if (!this._ddNotificationCodes.find(c => c.original.code === pay.reasonCode)) {
          pay.reasonCode = null;
        }
      }
    }

    this._updateValidation();
  }

  private _getBulkCode(type: PaymentType): string {
    let code: string = null;

    if (this.dataSource && this.dataSource.source.value.length) {
      for (const pay of this.dataSource.source.value) {
        if (pay.payItem.paymentType === type) {
          if (code === null) {
            if (pay.reasonCode) {
              code = pay.reasonCode;
            } else {
              break;
            }
          } else if (pay.reasonCode !== code) {
            code = null;
            break;
          }
        }
      }
    }

    return code;
  }

  private _setBulkCode(type: PaymentType, validationCodes: TranslatedBankNotificationCode[], code: string) {
    if (!validationCodes.find(c => c.original.code === code)) {
      return;
    }

    for (const pay of this.dataSource.source.value) {
      if (pay.payItem.paymentType === type) {
        pay.reasonCode = code;
      }
    }

    this._updateValidation();
  }
}
